import { createAction } from '@reduxjs/toolkit';
import type {
  CreateFaqCategoryRequest,
  CreateFaqQuestionRequest,
  FaqSearchMiss,
  UpdateFaqCategoryRequest,
  UpdateFaqQuestionRequest,
} from '@shared/schemas';

/**
 * "Signal" actions: intents the admin FAQ page dispatches for the saga to act on.
 * Kept out of the slice's reducers: they trigger side effects, not state
 * transitions.
 */

let lastRequestId = 0;

/** A fresh id for a write, to find its outcome in `outcomes` (see `MutationOutcome`). */
export function newRequestId(): string {
  lastRequestId += 1;
  return `faq-write-${lastRequestId}`;
}

/**
 * Every write carries a request id in `meta`, the caller's or a fresh one, so
 * its outcome can be matched to whoever asked: `deleteQuestionRequested(id)`, or
 * `deleteQuestionRequested(id, requestId)` from a dialog that waits for it.
 */
function withRequestId<P>() {
  return (payload: P, requestId: string = newRequestId()) => ({ payload, meta: { requestId } });
}

/** Load the whole FAQ tree (dispatched on mount). */
export const loadRequested = createAction('adminFaq/loadRequested');

export const createCategoryRequested = createAction(
  'adminFaq/createCategoryRequested',
  withRequestId<CreateFaqCategoryRequest>(),
);
export const updateCategoryRequested = createAction(
  'adminFaq/updateCategoryRequested',
  withRequestId<{
    id: string;
    changes: UpdateFaqCategoryRequest;
  }>(),
);
/** Soft delete; the page offers Undo through `lastDeleted`. */
export const deleteCategoryRequested = createAction(
  'adminFaq/deleteCategoryRequested',
  withRequestId<string>(),
);
export const restoreCategoryRequested = createAction(
  'adminFaq/restoreCategoryRequested',
  withRequestId<string>(),
);
/** Move a category to `toIndex` among the categories (Move up / Move down, later drag and drop). */
export const moveCategoryRequested = createAction(
  'adminFaq/moveCategoryRequested',
  withRequestId<{ id: string; toIndex: number }>(),
);

export const createQuestionRequested = createAction(
  'adminFaq/createQuestionRequested',
  withRequestId<CreateFaqQuestionRequest>(),
);
export const updateQuestionRequested = createAction(
  'adminFaq/updateQuestionRequested',
  withRequestId<{
    id: string;
    changes: UpdateFaqQuestionRequest;
  }>(),
);
/** Soft delete; the page offers Undo through `lastDeleted`. */
export const deleteQuestionRequested = createAction(
  'adminFaq/deleteQuestionRequested',
  withRequestId<string>(),
);
export const restoreQuestionRequested = createAction(
  'adminFaq/restoreQuestionRequested',
  withRequestId<string>(),
);
/**
 * Move a question into `categoryId` (its own or another) at `index` among that
 * category's questions: Move up / Move down, "Move to…", later drag and drop.
 */
export const moveQuestionRequested = createAction(
  'adminFaq/moveQuestionRequested',
  withRequestId<{
    id: string;
    categoryId: string;
    index: number;
  }>(),
);

/**
 * Dismiss a search with no answer (#735): off the list at once, then the server.
 * Not part of the write queue: it touches no category nor question. `index` is
 * where it sat, for its Undo.
 */
export const searchMissDismissRequested = createAction<{ item: FaqSearchMiss; index: number }>(
  'adminFaq/searchMissDismissRequested',
);
/** The Undo of a dismiss: back on the list at once, then the server, by its stamp. */
export const searchMissRestoreRequested = createAction<{
  item: FaqSearchMiss;
  index: number;
  dismissedAt: string;
}>('adminFaq/searchMissRestoreRequested');
